import { JobCompletionCountResult } from "@app/operations/jobStatisticsResult";
import { JobCompletionGranularity, JobCompletionPoint } from "@app/operations/job-completion-rate/types";

const BUCKET_MINUTES: Record<JobCompletionGranularity, number> = {
  [JobCompletionGranularity.FifteenMinute]: 15,
  [JobCompletionGranularity.Hour]: 60,
};

/**
 * Upper bound on plotted buckets. A wide range at 15-minute granularity (a year is ~35,000 buckets)
 * would otherwise stall the chart, so gap filling is skipped past this and the caller says so.
 */
const MAX_FILLED_BUCKETS = 3000;

interface JobCompletionChartData {
  /** False when the range was too wide to fill, so quiet intervals are missing from `points`. */
  gapsFilled: boolean;
  points: JobCompletionPoint[];
}

/** Formats a timestamp the way the API serialises bucket starts: local wall-clock, no zone. */
function toBucketStart(epochMs: number): string {
  const date = new Date(epochMs);
  const pad = (value: number): string => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:00`;
}

function emptyPoint(bucketStart: string): JobCompletionPoint {
  return { bucketStart, failureCount: 0, failureTrend: null, successCount: 0, successTrend: null };
}

/**
 * The SQL GROUP BY only returns buckets that contain completed jobs, so an interval where nothing
 * finished is absent and the line would connect straight across it — hiding the very stall this
 * chart exists to show. This inserts the missing buckets at zero.
 */
function fillBucketGaps(points: JobCompletionPoint[], granularity: JobCompletionGranularity): JobCompletionChartData {
  if (points.length < 2) {
    return { gapsFilled: true, points };
  }

  const stepMs = BUCKET_MINUTES[granularity] * 60 * 1000;
  const spanMs = new Date(points[points.length - 1].bucketStart).getTime() - new Date(points[0].bucketStart).getTime();
  const expectedBucketCount = Math.round(spanMs / stepMs) + 1;

  if (expectedBucketCount > MAX_FILLED_BUCKETS) {
    // Too wide to plot every bucket. A contiguous range of this size has nothing missing, so only
    // report unfilled gaps when buckets are genuinely absent.
    return { gapsFilled: points.length >= expectedBucketCount, points };
  }

  const filled: JobCompletionPoint[] = [];
  points.forEach((point, index) => {
    filled.push(point);

    const nextPoint = points[index + 1];
    if (!nextPoint) {
      return;
    }

    const nextMs = new Date(nextPoint.bucketStart).getTime();
    for (let gapMs = new Date(point.bucketStart).getTime() + stepMs; gapMs < nextMs; gapMs += stepMs) {
      filled.push(emptyPoint(toBucketStart(gapMs)));
    }
  });

  return { gapsFilled: true, points: filled };
}

/**
 * Least-squares linear fit over a value sequence, returning the fitted value at each index.
 * Recharts has no trendline primitive, so the trend is computed here and drawn as its own line.
 */
function calculateTrendLine(values: number[]): number[] | null {
  const count = values.length;
  if (count < 2) {
    return null;
  }

  const meanX = (count - 1) / 2;
  const meanY = values.reduce((total, value) => total + value, 0) / count;

  let covariance = 0;
  let varianceX = 0;
  values.forEach((value, index) => {
    covariance += (index - meanX) * (value - meanY);
    varianceX += (index - meanX) ** 2;
  });

  const slope = varianceX === 0 ? 0 : covariance / varianceX;
  const intercept = meanY - slope * meanX;

  // Clamped at zero because a fitted value can go negative, and a negative job count is not plottable
  return values.map((_, index) => Math.max(0, Math.round((intercept + slope * index) * 100) / 100));
}

/**
 * Folds the success and failure rows the API returns for each time bucket into a single chart point
 * per bucket, inserts zero-valued buckets for intervals the query returned nothing for, then
 * appends the trend value for each series. Trends are fitted after filling so a quiet interval
 * pulls the trend down rather than being skipped over.
 */
function toJobCompletionPoints(results: JobCompletionCountResult[], granularity: JobCompletionGranularity): JobCompletionChartData {
  const pointsByBucket = new Map<string, JobCompletionPoint>();

  results.forEach(result => {
    const point = pointsByBucket.get(result.bucketStart) ?? {
      bucketStart: result.bucketStart,
      failureCount: 0,
      failureTrend: null,
      successCount: 0,
      successTrend: null,
    };

    if (result.success) {
      point.successCount += result.completedJobCount;
    } else {
      point.failureCount += result.completedJobCount;
    }

    pointsByBucket.set(result.bucketStart, point);
  });

  const sorted = Array.from(pointsByBucket.values())
    .sort((a, b) => new Date(a.bucketStart).getTime() - new Date(b.bucketStart).getTime());

  const { gapsFilled, points } = fillBucketGaps(sorted, granularity);
  const successTrend = calculateTrendLine(points.map(point => point.successCount));
  const failureTrend = calculateTrendLine(points.map(point => point.failureCount));

  return {
    gapsFilled,
    points: points.map((point, index) => ({
      ...point,
      failureTrend: failureTrend ? failureTrend[index] : null,
      successTrend: successTrend ? successTrend[index] : null,
    })),
  };
}

export { calculateTrendLine, JobCompletionChartData, MAX_FILLED_BUCKETS, toJobCompletionPoints };
